import { useCallback, useState } from 'react';
import { ScrollView } from 'react-native';
import {
  Chip,
  Flex,
  RemoteImage,
  Typography,
  useScreenSize,
} from '@banhala/ui-kit';
import type { GoodsCarouselCalendarProps } from './types';
import { bridge } from '../../../../utils';
import { useLogClickGoods } from './hooks/useLogClickGoods';
import { localizeNumber } from '../../../../utils';
import { useOpenDeeplink } from '../../../../hooks';
import type { IScreensLogging } from '../../../../api/types/IScreenLogging';

export const GoodsCarouselCalendar = ({
  entity,
  logging,
}: GoodsCarouselCalendarProps) => {
  const [selectedIndex, setSelectedIndex] = useState(0);

  if (!entity || entity.length === 0) return null;

  const selectedEntity = entity[selectedIndex];
  const goodsList = selectedEntity?.item.goods_list ?? [];

  return (
    <Flex>
      {/* 필터 칩 */}
      {entity.length > 1 && (
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={{
            paddingHorizontal: 14,
            gap: 8,
            paddingBottom: 12,
          }}
        >
          {entity.map((item, index) => {
            const isSelected = index === selectedIndex;
            return (
              <Flex key={index}>
                <Chip
                  size="small"
                  state={isSelected ? 'selected' : 'enabled'}
                  onPress={() => {
                    bridge.logAnalyticsEvent({
                      name: 'CLICK_CAROUSEL_FILTER',
                      analytics: {
                        ...logging?.analytics,
                        ...item.logging?.analytics,
                        SELECTED_INDEX: index,
                      },
                      ably_only_log: { ...logging?.ably_only_log, ...item.logging?.ably_only_log },
                    });
                    setSelectedIndex(index);
                  }}
                >
                  {formatCalendarLabel(item.item.finished_at)}
                </Chip>
              </Flex>
            );
          })}
        </ScrollView>
      )}

      {/* 상품 캐러셀 */}
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={{
          paddingHorizontal: 16,
          gap: 8,
        }}
      >
        {goodsList.map((goods, index) => (
          <GoodsCard
            key={goods.item.sno}
            goods={goods.item}
            goodsLogging={goods.logging}
            logging={logging}
            index={index}
          />
        ))}
      </ScrollView>
    </Flex>
  );
};

const GoodsCard = ({
  goods,
  goodsLogging,
  logging,
  index,
}: {
  goods: { sno: number; name: string; thumbnail: string; deeplink: string; price: number; discount_rate: number };
  goodsLogging: IScreensLogging;
  logging: IScreensLogging;
  index: number;
}) => {
  const { width: screenWidth } = useScreenSize();
  const cardWidth = (screenWidth - 48) / 2.5;
  const { openDeeplink } = useOpenDeeplink();
  const logClickGoods = useLogClickGoods(logging);

  const handlePress = useCallback(() => {
    logClickGoods(goodsLogging, { INDEX: index });
    openDeeplink(goods.deeplink);
  }, [goods.deeplink, goodsLogging, index, logClickGoods, openDeeplink]);

  return (
    <Flex width={cardWidth} onPress={handlePress}>
      <Flex borderRadius={4} overflow="hidden">
        <RemoteImage
          uri={goods.thumbnail}
          width={cardWidth}
          height={cardWidth * 1.2}
          resizeMode="cover"
        />
      </Flex>
      <Flex padding={{ top: 8 }} gap={2}>
        <Typography type="body4" color="content_primary" numberOfLines={2}>
          {goods.name}
        </Typography>
        <Flex.Row alignItems="center" gap={4}>
          {goods.discount_rate > 0 && (
            <Typography type="subtitle2" color="content_pink_primary">
              {goods.discount_rate}%
            </Typography>
          )}
          <Typography type="subtitle2" color="content_primary">
            {localizeNumber(goods.price)}원
          </Typography>
        </Flex.Row>
      </Flex>
    </Flex>
  );
};

const formatCalendarLabel = (finishedAt: string): string => {
  const date = new Date(finishedAt);
  const month = date.getMonth() + 1;
  const day = date.getDate();
  return `${month}/${day}`;
};
